// Sign-in calls against Better Auth (/api/auth/*) for server mode. Sample mode has no server, so callers check
// `serverAuth` and keep their sample behaviour when it is false.
export const serverAuth = import.meta.env.VITE_DATA === "server";

export type SignInResult = { ok: true; twoFactor?: boolean } | { ok: false; status: number; code?: string };

// Cloudflare Turnstile on the sign-in forms (operator 2026-10-29): the AuthCheck widget stores its token here and the next
// call sends it as x-captcha-response, which the server checks when its secret is set. A token works once, so every widget
// is told to fetch a new one after a call used it.
let captchaToken: string | null = null;
const captchaUsed = new Set<() => void>();
export const setCaptchaToken = (token: string | null) => {
  captchaToken = token;
};
export const onCaptchaUsed = (fn: () => void) => {
  captchaUsed.add(fn);
  return () => {
    captchaUsed.delete(fn);
  };
};

// While a check widget is on the page, a call waits for its token instead of going out without one and being refused:
// Cloudflare may still be verifying, or waiting for the person to tick its box (2026-10-31, the owner was refused on
// production because the hidden widget wanted a click).
let captchaWidgets = 0;
export const captchaMounted = (on: boolean) => {
  captchaWidgets = Math.max(0, captchaWidgets + (on ? 1 : -1));
};
// Cloudflare drew its box only after about 20 s on the owner's network (2026-10-31), so a call waits up to 30 s
export const CAPTCHA_WAIT_MS = 30_000;
async function captchaSettled(ms: number): Promise<void> {
  const until = Date.now() + ms;
  while (captchaWidgets > 0 && captchaToken === null && Date.now() < until) await new Promise((r) => setTimeout(r, 150));
}

/** POST to Better Auth with the Turnstile token (x-captcha-response) when a check is on the page; shared by sign-in and the reset request. */
export async function post(path: string, body: unknown): Promise<Response> {
  await captchaSettled(CAPTCHA_WAIT_MS);
  const token = captchaToken;
  captchaToken = null;
  const headers: Record<string, string> = { "content-type": "application/json", ...(token ? { "x-captcha-response": token } : {}) };
  try {
    return await fetch(`/api/auth${path}`, { method: "POST", credentials: "same-origin", headers, body: JSON.stringify(body) });
  } finally {
    if (token) for (const fn of captchaUsed) fn();
  }
}

/** The signed-in account: null when signed out, undefined when the server could not be asked. */
export async function currentUser(): Promise<{ id: string; email: string } | null | undefined> {
  try {
    const res = await fetch("/api/auth/get-session", { credentials: "same-origin" });
    if (!res.ok) return res.status === 401 ? null : undefined;
    const body = (await res.json().catch(() => null)) as { user?: { id: string; email: string } } | null;
    return body?.user ?? null;
  } catch {
    return undefined;
  }
}

// once a page has seen the session, later page changes do not ask again (signing out reloads the app)
let sessionSeen = false;

/** Whether this browser has a session: asked once per page load, before a team or portal page draws, so a signed-out
 *  visitor goes straight to sign-in instead of seeing the app first. Undefined when the server could not be asked. */
export async function hasSession(): Promise<boolean | undefined> {
  if (sessionSeen) return true;
  const user = await currentUser();
  if (user === undefined) return undefined;
  // a session the auth library does not hold (the dev sign-in the headless tour run uses) still reads the workspace
  const signedIn = user !== null || (await workspaceAnswers());
  if (signedIn === undefined) return undefined;
  if (signedIn) sessionSeen = true;
  return signedIn;
}

/** Whether GET /api/v1/workspace answers for this browser: true on 2xx, false on any other status, undefined when unreachable. */
async function workspaceAnswers(): Promise<boolean | undefined> {
  const res = await fetch("/api/v1/workspace", { credentials: "same-origin" }).catch(() => null);
  return res === null ? undefined : res.ok;
}

/** X15-0102c: a signed-in client on the team address is handed to the client dashboard. Answers the one-time link to follow
 *  (the team session ends on the server), or null when this person stays here, or when the server could not be asked. */
export async function clientHandoff(): Promise<string | null> {
  try {
    const res = await fetch("/api/auth/offsilo/client-handoff", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: "{}" });
    if (!res.ok) return null;
    const body = (await res.json().catch(() => null)) as { url?: unknown } | null;
    return typeof body?.url === "string" ? body.url : null;
  } catch {
    return null;
  }
}

export type SignInProvider = "google" | "microsoft";

/** The providers whose keys are set on the server, for the team sign-in and invite pages. None in sample mode. */
export async function signInProviders(): Promise<SignInProvider[]> {
  if (!serverAuth) return [];
  try {
    const res = await fetch("/client-auth/providers?staff=1", { credentials: "same-origin" });
    const body = res.ok ? ((await res.json()) as { providers?: string[] }) : {};
    return (body.providers ?? []).filter((p): p is SignInProvider => p === "google" || p === "microsoft");
  } catch {
    return [];
  }
}

/** Leaves for the provider's sign-in; the server brings the person back to `next`, signed in. */
export const startProviderSignIn = (provider: SignInProvider, next = "/home") =>
  window.location.assign(`/client-auth/${provider}/start?staff=1&next=${encodeURIComponent(next)}`);

/** The answers of the server's Turnstile check: the sign-in was not tried, so the person should pass the check again. */
const CHECK_CODES = new Set(["MISSING_RESPONSE", "VERIFICATION_FAILED", "SERVICE_UNAVAILABLE"]);
export const checkRefused = (r: SignInResult) => !r.ok && r.code !== undefined && CHECK_CODES.has(r.code);

async function result(res: Response): Promise<SignInResult> {
  const body = (await res.json().catch(() => ({}))) as { twoFactorRedirect?: boolean; code?: string };
  if (!res.ok) return { ok: false, status: res.status, code: body.code };
  return { ok: true, twoFactor: body.twoFactorRedirect === true };
}

/** Email and password. A two-factor account answers `twoFactor: true`; continue with verifyCode. */
export const signInWithPassword = async (email: string, password: string) => result(await post("/sign-in/email", { email, password }));

/** Sends a sign-in link to the address (the server mails it through the platform mailbox). */
export const sendSignInLink = async (email: string, callbackURL = "/home", errorCallbackURL = "/sign-in") =>
  result(await post("/sign-in/magic-link", { email, callbackURL, errorCallbackURL }));

/** A single sign-on answer that did not start: `refused` carries the server's own sentence (not set up, too many tries);
 *  `error` means the service could not be reached or failed, and the person can try again. */
export class SsoError extends Error {
  constructor(readonly kind: "refused" | "error", message: string) {
    super(message);
  }
}

/** Whether single sign-on covers this address's domain. The answer is only { sso: boolean }; it names no provider. */
export async function ssoCovers(email: string): Promise<boolean> {
  const res = await fetch("/api/public/sso/check", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ email }) });
  if (!res.ok) throw new SsoError("error", "The check did not answer.");
  const body = (await res.json().catch(() => ({}))) as { sso?: unknown };
  return body.sso === true;
}

/** Starts a sign-in with the workspace's identity provider and returns the address to send the browser to. The email goes in
 *  the JSON body only. */
export async function startSso(email: string, next = "/home"): Promise<string> {
  let res: Response;
  try {
    res = await fetch("/api/public/sso/start", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ email, next }) });
  } catch {
    throw new SsoError("error", "We could not reach the sign-in service. Try again in a moment.");
  }
  const body = (await res.json().catch(() => ({}))) as { redirectUrl?: unknown; detail?: unknown; code?: unknown };
  if (res.ok && typeof body.redirectUrl === "string" && body.redirectUrl.startsWith("https://")) return body.redirectUrl;
  const sentence = typeof body.detail === "string" && body.detail ? body.detail : undefined;
  // 503 (not set up) and 429 (too many tries) carry a sentence to show; anything else can be retried
  if (sentence && (res.status === 503 || res.status === 429)) throw new SsoError("refused", sentence);
  throw new SsoError("error", "We could not reach the sign-in service. Try again in a moment.");
}

/** The six-digit code from the authenticator app, after a password sign-in that asked for it. */
export const verifyCode = async (code: string) => result(await post("/two-factor/verify-totp", { code }));

/** Whether this browser can use passkeys at all. */
export const passkeysSupported = () => typeof window !== "undefined" && typeof window.PublicKeyCredential === "function";

/** Signs in with a passkey: the browser asks which one, the server checks it and starts a session. */
export async function signInWithPasskey(): Promise<SignInResult> {
  const { startAuthentication } = await import("@simplewebauthn/browser");
  const opts = await fetch("/api/auth/passkey/generate-authenticate-options", { credentials: "same-origin" });
  if (!opts.ok) return { ok: false, status: opts.status };
  try {
    const response = await startAuthentication({ optionsJSON: await opts.json() });
    return result(await post("/passkey/verify-authentication", { response }));
  } catch {
    return { ok: false, status: 0, code: "cancelled" }; // the person closed the browser's dialog
  }
}

/** One passkey on the signed-in account. */
export type Passkey = { id: string; name?: string | null; createdAt?: string; deviceType: string; backedUp: boolean };

/** Registers a passkey on the signed-in account (needs a recent sign-in). */
export async function addPasskey(name: string): Promise<SignInResult> {
  const { startRegistration } = await import("@simplewebauthn/browser");
  const opts = await fetch("/api/auth/passkey/generate-register-options", { credentials: "same-origin" });
  if (!opts.ok) {
    const body = (await opts.json().catch(() => ({}))) as { code?: string };
    return { ok: false, status: opts.status, code: body.code };
  }
  try {
    const response = await startRegistration({ optionsJSON: await opts.json() });
    return result(await post("/passkey/verify-registration", { response, name }));
  } catch {
    return { ok: false, status: 0, code: "cancelled" };
  }
}

export async function listPasskeys(): Promise<Passkey[]> {
  const res = await fetch("/api/auth/passkey/list-user-passkeys", { credentials: "same-origin" });
  if (!res.ok) throw new Error(`Could not load passkeys (${res.status})`);
  return (await res.json()) as Passkey[];
}

export const deletePasskey = async (id: string) => (await post("/passkey/delete-passkey", { id })).ok;

export const signOut = async () => {
  await post("/sign-out", {});
};

/** One signed-in device: `current` is this browser. No session token is ever sent to the page. */
export type AuthSession = { id: string; createdAt: string; updatedAt: string; expiresAt: string; ipAddress?: string | null; userAgent?: string | null; current: boolean };

/** The signed-in person's devices, most recently active first (GET /api/v1/session/devices). */
export async function listSessions(): Promise<AuthSession[]> {
  const res = await fetch("/api/v1/session/devices", { credentials: "same-origin" });
  if (!res.ok) throw new Error(`Could not load devices (${res.status})`);
  return (await res.json()) as AuthSession[];
}

const v1 = (path: string) => fetch(`/api/v1${path}`, { method: "POST", credentials: "same-origin" });

/** Signs one other device out, by its session id. */
export const revokeSession = async (id: string) => (await v1(`/session/devices/${encodeURIComponent(id)}/revoke`)).ok;

/** Signs out every device except this one. */
export const revokeOtherSessions = async () => (await v1("/session/devices/revoke-others")).ok;

async function failed(res: Response): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as { message?: unknown };
  throw new Error(typeof body.message === "string" && body.message ? body.message : `Request failed (${res.status})`);
}

/** X15-0103d: asks for a new sign-in email; the change happens when the person opens the link mailed to the new address. */
export async function changeEmail(newEmail: string): Promise<void> {
  const res = await post("/change-email", { newEmail, callbackURL: "/settings/account/sign-in?email=changed" });
  if (!res.ok) await failed(res);
}

/** X15-0103d: changes the signed-in person's password; `signOutOthers` ends their other sessions. */
export async function changePassword(current: string, next: string, signOutOthers: boolean): Promise<void> {
  const res = await post("/change-password", { currentPassword: current, newPassword: next, revokeOtherSessions: signOutOthers });
  if (!res.ok) await failed(res);
}
